/**
 * Partner finder page component.
 *
 * Main page for the partner finder feature providing tabbed navigation between
 * finding partners, managing requests, and messaging. Handles route-based
 * tab selection and provides primary action button for creating new requests.
 *
 * Routes: `/partners`, `/partners/find`, `/partners/request`, `/partners/messages`
 * Requires authentication: Yes
 *
 * @module PartnerFinder
 */

import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FindPartners from "@/components/partner-finder/FindPartners";
import MyRequest from "@/components/partner-finder/MyRequest";
import Messages from "@/components/partner-finder/Messages";
import { Plus } from "lucide-react";
import { usePartnerFinderPath } from "@/hooks/usePartnerFinderPath";
import { useAuth } from "@/hooks/useAuth";
import { usePartnerRequests } from "@/hooks/usePartnerRequests";
import { ROUTES } from "@/lib/routeConstants";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";

export default function PartnerFinder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const paths = usePartnerFinderPath();
  const { requests } = usePartnerRequests(user?.id);

  // Check if user has an active request
  const hasActiveRequest = requests.some((r) => r.status === "active");

  // Determine active tab from URL
  const getActiveTab = () => {
    if (location.pathname.includes(ROUTES.PARTNERS_MESSAGES)) return "messages";
    if (location.pathname.includes(ROUTES.PARTNERS_REQUEST)) return "request";
    return "find";
  };

  const activeTab = getActiveTab();

  const handleTabChange = (value: string) => {
    if (value === "find") {
      navigate(paths.find);
    } else if (value === "request") {
      navigate(paths.request);
    } else if (value === "messages") {
      navigate(paths.messages);
    }
  };

  const handlePrimaryAction = () => {
    // If user has an active request, navigate to create a new one (add mode)
    // If no active request, navigate to create (same route, but form will be empty)
    if (hasActiveRequest) {
      navigate(paths.requestEdit, { state: { action: "add" } });
    } else {
      navigate(paths.requestEdit);
    }
  };

  return (
    <StandardPageShell
      title={t("partnerFinder.title")}
      headerRight={<LanguageSwitcher />}
      className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8"
    >
      <div>
        {/* Page Header */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 lg:mb-8">
          <div>
            <h1 className="hidden text-4xl font-bold tracking-tight text-foreground lg:block">
              {t("partnerFinder.title")}
            </h1>
            <p className="mt-0 text-sm text-muted-foreground lg:mt-2 lg:text-base">
              {t("partnerFinder.subtitle")}
            </p>
          </div>
          {activeTab !== "messages" && (
            <Button
              onClick={handlePrimaryAction}
              className="w-full sm:w-auto h-11 sm:h-auto text-base sm:text-sm"
            >
              {hasActiveRequest ? (
                <>
                  <Plus className="h-5 w-5 sm:h-4 sm:w-4 mr-2" />
                  {t("partnerFinder.buttons.submitRequest", {
                    defaultValue: "Add Request",
                  })}
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 sm:h-4 sm:w-4 mr-2" />
                  {t("partnerFinder.buttons.submitRequest", {
                    defaultValue: "Create Request",
                  })}
                </>
              )}
            </Button>
          )}
        </div>

        {/* Sub-navigation - Desktop */}
        <div className="mb-6 hidden lg:mb-8 lg:block">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="w-full justify-start bg-muted/50">
              <TabsTrigger value="find" className="px-6">
                {t("partnerFinder.tabs.find")}
              </TabsTrigger>
              <TabsTrigger value="request" className="px-6">
                {t("partnerFinder.tabs.myRequests")}
              </TabsTrigger>
              <TabsTrigger value="messages" className="px-6">
                {t("partnerFinder.tabs.messages", { count: 0 })
                  .replace("(0)", "")
                  .trim()}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Mobile: Segmented Control */}
        <div className="mb-4 lg:hidden">
          <div className="inline-flex w-full min-w-0 rounded-lg bg-muted p-1">
            <button
              onClick={() => handleTabChange("find")}
              className={`min-h-11 min-w-0 flex-1 rounded-md px-2 py-2 text-xs font-medium transition-colors sm:px-4 sm:text-sm ${
                activeTab === "find"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("partnerFinder.tabs.find")}
            </button>
            <button
              onClick={() => handleTabChange("request")}
              className={`min-h-11 min-w-0 flex-1 rounded-md px-2 py-2 text-xs font-medium transition-colors sm:px-4 sm:text-sm ${
                activeTab === "request"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("partnerFinder.tabs.myRequests")}
            </button>
            <button
              onClick={() => handleTabChange("messages")}
              className={`min-h-11 min-w-0 flex-1 rounded-md px-2 py-2 text-xs font-medium transition-colors sm:px-4 sm:text-sm ${
                activeTab === "messages"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("partnerFinder.tabs.messages", { count: 0 })
                .replace("(0)", "")
                .trim()}
            </button>
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === "find" && <FindPartners />}
          {activeTab === "request" && <MyRequest />}
          {activeTab === "messages" && <Messages />}
        </div>
      </div>
    </StandardPageShell>
  );
}
